export interface BrandConfig {
  companyName: string;
  productName: string;
  /**
   * Official logo files (DESIGN_SYSTEM.md §9, PRD §3.12): exact exports in
   * public/brand/, used through <BrandLogo>. `*OnDark` are the white variants.
   * A white-label tenant that sets VITE_BRAND_LOGO_URL uses that one file for
   * every variant (and VITE_BRAND_LOGO_DARK_URL on dark surfaces).
   */
  logos: {
    mark: string;
    lockup: string;
    symbol: string;
    markOnDark: string;
    lockupOnDark: string;
    symbolOnDark: string;
  };
  /** Decorative login brand-panel pattern (§9.3); empty = plain panel. */
  loginPatternUrl: string;
  /** The same pattern recoloured for dark (A10); empty = plain panel in dark. */
  loginPatternDarkUrl: string;
  faviconUrl: string;
  /** Action colour (primary button). Informational: the UI reads the scale tokens. */
  primaryColor: string;
  /** Brand mark colour (logo arc and dot). Informational: the UI reads the scale tokens. */
  accentColor: string;
  surfaceBase: string;
  /** Browser UI colour (meta theme-color). White by default (Q-014b). */
  themeColor: string;
  emailHeaderColor: string;
  browserTitle: string;
  loginTitle: string;
  loginSubtitle: string;
  copyrightText: string;
  supportUrl?: string;
  primaryScale: {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
    950: string;
  };
  shadowTint: string;
}

const OTE_LOGOS: BrandConfig['logos'] = {
  mark: '/brand/ote-mark.svg',
  lockup: '/brand/ote-lockup.svg',
  symbol: '/brand/ote-symbol.svg',
  markOnDark: '/brand/ote-mark-white.svg',
  lockupOnDark: '/brand/ote-lockup-white.svg',
  symbolOnDark: '/brand/ote-symbol-white.svg',
};

export const defaultBrand: BrandConfig = {
  companyName: 'OTE',
  productName: 'O.T.E Connect',
  logos: OTE_LOGOS,
  loginPatternUrl: '/brand/login-pattern-a.svg',
  loginPatternDarkUrl: '/brand/login-pattern-a-dark.svg',
  faviconUrl: '/brand/favicon-optical.svg',
  // Official palette (Q-014b, PRD §3.2): action #2F5EE6 (scale 600), brand mark #406EF1 (scale 500).
  primaryColor: '#2F5EE6',
  accentColor: '#406EF1',
  // The brand-tinted canvas ("Brand Air", 2026-10-06) = brandGround(primaryScale)[50].
  // Mirrors --c-tint-50 in tokens.css and the PWA manifest background_color.
  surfaceBase: '#E9EFFA',
  themeColor: '#FFFFFF',
  emailHeaderColor: '#2F5EE6',
  // Tenant branding defaults (overridable per deployment via VITE_BRAND_*), not i18n text.
  browserTitle: 'O.T.E Connect',
  loginTitle: 'התחברות למערכת',
  loginSubtitle: 'מערכת ניהול שירותים',
  copyrightText: '© 2026 O.T.E Connect',
  // Brand scale v2, hue 266 from the official #406EF1 (PRD §3.2). Mirrors src/styles/tokens.css.
  primaryScale: {
    50: '#F3F7FF',
    100: '#E6EEFF',
    200: '#CCDCFF',
    300: '#A8C2FE',
    400: '#739BFD',
    500: '#406EF1',
    600: '#2F5EE6',
    700: '#284CC3',
    800: '#1F3C9C',
    900: '#162B6F',
    950: '#0D1B45',
  },
  shadowTint: 'rgba(21, 25, 35, 0.04)',
};

/**
 * Converts a CSS colour to the RGB-channel form used by src/styles/tokens.css
 * ("R G B", e.g. "#406EF1" → "64 110 241"), so Tailwind's `rgb(var(--c-…) / α)`
 * keeps working for white-label values. Accepts #rgb, #rrggbb (alpha digits
 * #rgba / #rrggbbaa are ignored: tokens are opaque) and rgb()/rgba().
 * Returns null for anything else; the caller decides the fallback.
 */
export function toRgbChannels(color: string): string | null {
  const value = color.trim();
  const hex = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(value);
  if (hex) {
    let digits = hex[1];
    if (digits.length <= 4) digits = digits.split('').map((d) => d + d).join('');
    return [0, 2, 4].map((i) => parseInt(digits.slice(i, i + 2), 16)).join(' ');
  }
  const rgb = /^rgba?\(\s*(\d{1,3})[\s,]+(\d{1,3})[\s,]+(\d{1,3})\s*(?:[,/]\s*[\d.]+%?\s*)?\)$/i.exec(value);
  if (rgb) return [rgb[1], rgb[2], rgb[3]].map((n) => Math.min(255, Number(n))).join(' ');
  return null;
}

/** Mixes two #rrggbb colours (t = share of `b`), rounded per channel. */
export function mixHex(a: string, b: string, t: number): string {
  const ch = (hex: string) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  const [ca, cb] = [ch(a), ch(b)];
  return `#${ca.map((v, i) => Math.round(v * (1 - t) + cb[i] * t).toString(16).padStart(2, '0')).join('').toUpperCase()}`;
}

/**
 * Cool neutral bases of the brand-tinted ground (DESIGN_SYSTEM.md §2.3, "Brand Air").
 * Each is mixed 50 / 50 with the scale's 100 step, so a white-label scale tints
 * the canvas, the subtle fill and the decorative line in its own hue.
 */
export const GROUND_BASES = { 50: '#ECF0F5', 100: '#E6E8ED', 200: '#C4CCD7' } as const;

/** The tinted ground for a brand scale: 50 = canvas, 100 = subtle, 200 = line. */
export function brandGround(scale: Pick<BrandConfig['primaryScale'], 100>): { 50: string; 100: string; 200: string } {
  const tint = /^#[0-9a-f]{6}$/i.test(scale[100]) ? scale[100] : defaultBrand.primaryScale[100];
  return {
    50: mixHex(GROUND_BASES[50], tint, 0.5),
    100: mixHex(GROUND_BASES[100], tint, 0.5),
    200: mixHex(GROUND_BASES[200], tint, 0.5),
  };
}

export function loadBrandConfig(): BrandConfig {
  const env = import.meta.env;

  if (!env.VITE_BRAND_NAME) {
    return defaultBrand;
  }

  const primaryScale: BrandConfig['primaryScale'] = env.VITE_BRAND_PRIMARY_SCALE
    ? JSON.parse(env.VITE_BRAND_PRIMARY_SCALE)
    : defaultBrand.primaryScale;

  // One custom file replaces every variant; a tenant with its own logo gets no OTE pattern.
  const customLogo: string | undefined = env.VITE_BRAND_LOGO_URL;
  const customLogoDark: string | undefined = env.VITE_BRAND_LOGO_DARK_URL || customLogo;
  const logos: BrandConfig['logos'] = customLogo
    ? {
        mark: customLogo,
        lockup: customLogo,
        symbol: customLogo,
        markOnDark: customLogoDark || customLogo,
        lockupOnDark: customLogoDark || customLogo,
        symbolOnDark: customLogoDark || customLogo,
      }
    : defaultBrand.logos;

  return {
    companyName: env.VITE_BRAND_COMPANY_NAME || env.VITE_BRAND_NAME || defaultBrand.companyName,
    productName: env.VITE_BRAND_DISPLAY_NAME || env.VITE_BRAND_NAME || defaultBrand.productName,
    logos,
    loginPatternUrl:
      env.VITE_BRAND_LOGIN_PATTERN_URL ?? (customLogo ? '' : defaultBrand.loginPatternUrl),
    // A tenant's own light pattern has no OTE dark twin: plain panel in dark unless it gives one.
    loginPatternDarkUrl:
      env.VITE_BRAND_LOGIN_PATTERN_DARK_URL
      ?? (customLogo || env.VITE_BRAND_LOGIN_PATTERN_URL !== undefined ? '' : defaultBrand.loginPatternDarkUrl),
    faviconUrl: env.VITE_BRAND_FAVICON_URL || defaultBrand.faviconUrl,
    primaryColor: env.VITE_BRAND_PRIMARY_COLOR || defaultBrand.primaryColor,
    accentColor: env.VITE_BRAND_ACCENT_COLOR || defaultBrand.accentColor,
    surfaceBase: env.VITE_BRAND_SURFACE_BASE || brandGround(primaryScale)[50],
    themeColor: env.VITE_BRAND_THEME_COLOR || defaultBrand.themeColor,
    emailHeaderColor: env.VITE_BRAND_EMAIL_COLOR || env.VITE_BRAND_PRIMARY_COLOR || defaultBrand.emailHeaderColor,
    browserTitle: env.VITE_BRAND_BROWSER_TITLE || defaultBrand.browserTitle,
    loginTitle: env.VITE_BRAND_LOGIN_TITLE || defaultBrand.loginTitle,
    loginSubtitle: env.VITE_BRAND_LOGIN_SUBTITLE || defaultBrand.loginSubtitle,
    copyrightText: env.VITE_BRAND_COPYRIGHT || defaultBrand.copyrightText,
    supportUrl: env.VITE_BRAND_SUPPORT_URL,
    primaryScale,
    shadowTint: env.VITE_BRAND_SHADOW_TINT || defaultBrand.shadowTint,
  };
}
